import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@gapartners/ui';

/** One entry in the sent-digits log shown above the keypad. */
export interface DtmfEntry {
  digit: string;
  status: 'sent' | 'failed';
}

export interface DtmfKeypadProps {
  /** Digits sent so far, in order. Failed entries render in the error color. */
  entries: DtmfEntry[];
  /** When true, all keypad keys are disabled. */
  disabled?: boolean;
  onPress: (digit: string) => void;
}

/** Phone-style keypad order (1-9, then *, 0, #) — same order as OutboundDialPanel. */
// eslint-disable-next-line react-refresh/only-export-components
export const DTMF_DIGITS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
];

export function DtmfKeypad({
  entries,
  disabled = false,
  onPress,
}: DtmfKeypadProps) {
  const { t } = useTranslation();
  const lastEntry = entries[entries.length - 1];
  const showError = lastEntry?.status === 'failed';

  return (
    <Box
      sx={{
        width: 240,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      {/* Read-only log of sent digits */}
      <Box
        aria-label={t('components.amazonConnect.sentDigits', 'Sent digits')}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          px: 1.5,
          py: 1,
          minHeight: 40,
        }}
      >
        <Typography
          variant="body1"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {entries.map((entry, index) => (
            <Fragment key={index}>
              {index > 0 && ' '}
              <Box
                component="span"
                sx={{
                  color:
                    entry.status === 'failed' ? 'error.main' : 'text.primary',
                }}
              >
                {entry.digit}
              </Box>
            </Fragment>
          ))}
        </Typography>
      </Box>

      {/* Inline error message shown until the next key press */}
      {showError && (
        <Typography variant="caption" color="error.main">
          {t('components.amazonConnect.dtmfDigitNotSent', 'Digit not sent')}
        </Typography>
      )}

      {/* 3x4 phone keypad */}
      <Box
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}
      >
        {DTMF_DIGITS.map(digit => (
          <Button
            key={digit}
            variant="outlined"
            onClick={() => onPress(digit)}
            disabled={disabled}
            sx={{ minWidth: 56, fontSize: '1.25rem' }}
          >
            {digit}
          </Button>
        ))}
      </Box>
    </Box>
  );
}
